/**
 * Type definitions for analytics tracking
 * 
 * This file defines the expected properties for all analytics events
 * to ensure consistency and catch type errors.
 */

// User Properties
export const UserProperties = {
  // Required fields
  id: 'number',
  email: 'string',
  username: 'string',
  role: 'string', // STUDENT, TUTOR, ADMIN
  
  // Optional fields
  first_name: 'string',
  last_name: 'string',
  is_alpha_tester: 'boolean',
  email_verified: 'boolean',
  trial_exams_used: 'number',
  subscription_status: 'string',
  created_at: 'string',
  last_login: 'string',
  login_count: 'number'
};

// Event Properties
export const EventProperties = {
  // Subscription Events
  subscription_plan_viewed: {
    plan_id: 'string',
    plan_name: 'string',
    price_cents: 'number',
    exam_allowance: 'number|null',
    currency: 'string',
    has_active_subscription: 'boolean',
    is_alpha_tester: 'boolean'
  },
  
  subscription_checkout_started: {
    plan_id: 'string',
    plan_name: 'string',
    price_cents: 'number',
    price_dollars: 'number',
    exam_allowance: 'number|null',
    is_alpha_tester: 'boolean',
    has_active_subscription: 'boolean',
    source: 'string', // subscribe_button, subscription_plans
    page: 'string'
  },
  
  subscription_checkout_completed: {
    plan_id: 'string',
    plan_name: 'string',
    price_cents: 'number',
    price_dollars: 'number',
    exam_allowance: 'number|null',
    is_alpha_tester: 'boolean',
    subscription_status: 'string',
    user_role: 'string',
    user_id: 'number'
  },
  
  subscription_checkout_abandoned: {
    plan_id: 'string',
    plan_name: 'string',
    error_message: 'string',
    error_type: 'string', // stripe_redirect_error, api_error, payment_error
    source: 'string'
  },
  
  subscription_cancelled: {
    cancellation_reason: 'string', // immediate, end_of_period
    plan_id: 'string',
    plan_name: 'string',
    current_period_end: 'string',
    allowance_used: 'number',
    exam_allowance: 'number|null'
  },
  
  exam_limit_reached: {
    exam_count: 'number',
    exam_limit: 'number',
    has_active_subscription: 'boolean',
    is_alpha_tester: 'boolean',
    subscription_status: 'string'
  },
  
  subscription_prompt_shown: {
    prompt_type: 'string', // last_exam_warning, exam_limit_reached, exam_generation_blocked
    context: 'string', // generate_exam_page, generate_exam
    allowance_remaining: 'number',
    exam_allowance: 'number',
    is_alpha_tester: 'boolean',
    has_active_subscription: 'boolean',
    reason: 'string'
  },
  
  // Alpha Tester Events
  alpha_tester_registered: {
    referral_source: 'string|null', // referral_token or null
    referrer_id: 'number|null',
    is_email_verified: 'boolean',
    registration_source: 'string' // register_page, referral_landing
  },
  
  alpha_tester_onboarded: {
    days_since_registration: 'number',
    first_login: 'boolean',
    exams_generated: 'number'
  },
  
  alpha_referral_link_shared: {
    share_method: 'string', // copy, email, whatsapp
    referrals_used: 'number',
    referrals_remaining: 'number',
    max_referrals: 'number'
  },
  
  alpha_referral_visited: {
    referral_token: 'string',
    referrer_id: 'number',
    is_valid: 'boolean',
    error_reason: 'string|null' // expired, max_uses_reached, invalid_token
  },
  
  alpha_referral_converted: {
    referral_token: 'string',
    referrer_id: 'number',
    days_since_visit: 'number',
    registration_completed: 'boolean'
  },
  
  alpha_discount_applied: {
    coupon_code: 'string', // ALPHA90
    discount_percentage: 'number',
    plan_id: 'string',
    plan_name: 'string',
    original_price_cents: 'number',
    discounted_price_cents: 'number'
  },
  
  // Feature Gate Events
  feature_gate_encountered: {
    feature_name: 'string',
    required_plan: 'string',
    user_plan: 'string|null',
    is_alpha_tester: 'boolean',
    has_active_subscription: 'boolean'
  },
  
  // Payment Events
  payment_method_added: {
    payment_method_type: 'string', // card, bank_account
    payment_method_brand: 'string|null', // visa, mastercard, etc
    is_default: 'boolean'
  },
  
  payment_failed: {
    error_type: 'string', // card_declined, insufficient_funds, etc
    error_message: 'string',
    amount_cents: 'number',
    currency: 'string',
    subscription_id: 'string|null'
  },
  
  coupon_applied: {
    coupon_code: 'string',
    discount_percentage: 'number|null',
    discount_amount_cents: 'number|null',
    plan_id: 'string',
    plan_name: 'string'
  },
  
  trial_started: {
    trial_days: 'number',
    plan_id: 'string',
    plan_name: 'string',
    is_alpha_tester: 'boolean'
  },
  
  trial_ended: {
    trial_days_used: 'number',
    converted_to_paid: 'boolean',
    plan_id: 'string|null',
    plan_name: 'string|null'
  },
  
  trial_converted: {
    days_in_trial: 'number',
    plan_id: 'string',
    plan_name: 'string',
    price_cents: 'number',
    coupon_applied: 'boolean',
    coupon_code: 'string|null'
  },
  
  subscription_reactivated: {
    plan_id: 'string',
    plan_name: 'string',
    price_cents: 'number',
    days_since_cancellation: 'number'
  },
  
  subscription_plan_changed: {
    from_plan_id: 'string',
    from_plan_name: 'string',
    to_plan_id: 'string',
    to_plan_name: 'string',
    change_type: 'string', // upgrade, downgrade
    price_difference_cents: 'number'
  }
};

// Validation helper
export function validateEventProperties(eventName, properties) {
  const expectedProps = EventProperties[eventName];
  if (!expectedProps) {
    console.warn(`Unknown event: ${eventName}`);
    return;
  }
  
  // Check for missing required properties
  Object.keys(expectedProps).forEach(key => {
    if (!(key in properties)) {
      console.warn(`Missing property '${key}' for event '${eventName}'`);
    }
  });
  
  // Check for extra properties
  Object.keys(properties).forEach(key => {
    if (!(key in expectedProps) && !['timestamp', 'session_id'].includes(key)) {
      console.warn(`Unexpected property '${key}' for event '${eventName}'`);
    }
  });
}